import { useEffect, useState } from 'react';

import { closeModal, showToastMessage } from 'enable-ui';
import { setIsRefreshLearners } from 'stores';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { Institution } from 'modules/institutions/core/type';

import { checkRole, compileFormData, downloadURI } from 'helpers';
import { useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty } from 'lodash';

export const useImportLearnerController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id || '';

  const roleKey = userProfile?.role?.key || '';

  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const institutions =
    institutionState.state?.items?.map((elInstitutions: Institution) => ({
      value: elInstitutions.id,
      label: elInstitutions.name,
    })) || [];

  const [institutionValue, setInstitutionValue] = useState<string>('');

  const searchSelect = useString('');

  const [fileImport, setFileImport] = useState<File | null>(null);

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    if (roleKey === RoleKey.BIOLOGIC_ADMIN) {
      institutionState.fetchApi();
    }
  }, [roleKey]);

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    let newInstitutionId = institutionId;

    if (!isEmpty(institutions) && roleKey === RoleKey.BIOLOGIC_ADMIN) {
      newInstitutionId = institutions[0].value;
    }

    setInstitutionValue(newInstitutionId);
  }, [JSON.stringify(institutions), roleKey]);

  const onChangeSearchInstitution = (item: string) => {
    searchSelect.setValue(item);
  };

  const onChangeInstitution = (item: any) => {
    if (!item) {
      return;
    }

    setInstitutionValue(item.value);
  };

  const handleChangeFile = (file: File[]) => {
    setFileImport(file[0] || null);
  };

  const handleImportFile = async () => {
    const result: string[] = await apiCall({
      url: `/import/${institutionValue}`,
      method: 'POST',
      isLoading: true,
      showError: true,
      data: compileFormData({ file: fileImport }),
    });

    closeModal(MODAL_KEY.IMPORT_LEARNER);

    setFileImport(null);

    const messageError = result.filter((el) => el);

    const messageSuccess = result.filter((el) => !el);

    const usersError: string[] = [];

    messageError.forEach((el) => {
      if (el.includes('existed')) {
        const name = el.split(':')[0];

        usersError.push(name);
      } else {
        showToastMessage('error', el);
      }
    });

    if (usersError.length) {
      showToastMessage('error', `Clients ${usersError.join(', ')} existed!`);
    }

    if (!isEmpty(messageSuccess)) {
      showToastMessage('success', 'Imported clients successfully!');
    }

    setIsRefreshLearners(true);
  };

  const handleDownloadTemplate = (type: 'csv' | 'xlsx') => {
    const url = type === 'csv' ? 'template.csv' : 'template.xlsx';

    const fileName = type === 'csv' ? 'template.csv' : 'template.xlsx';

    downloadURI(url, fileName);
  };

  const isBiologicAdmin = checkRole([RoleKey.BIOLOGIC_ADMIN]);

  const disabled = isBiologicAdmin ? !fileImport || !institutionValue : !fileImport;

  const handleRemoveFile = () => {
    setFileImport(null);
  };

  return {
    institutions,
    institutionValue,
    searchSelect,
    onChangeInstitution,
    onChangeSearchInstitution,
    handleChangeFile,
    handleDownloadTemplate,
    handleImportFile,
    disabled,
    isBiologicAdmin,
    file: {
      name: fileImport?.name,
      size: fileImport?.size,
    },
    handleRemoveFile,
  };
};
